/**
 * Highlight system for the AbbreviationKey → button-finder feature.
 *
 * Flow:
 *   1. User taps an abbreviation on /key
 *   2. We stash a target descriptor in sessionStorage and navigate to the target page
 *   3. Target page calls `useHighlightTarget()` on mount, which:
 *      - Reads sessionStorage
 *      - Optionally opens a dialog (via the openDialog callback prop)
 *      - Adds `.ds-highlight-target` to the matching element after a small delay
 *      - Adds a one-shot pointerdown listener anywhere → clears the highlight
 *      - Shows an optional toast hint
 *      - Clears sessionStorage so it doesn't fire again
 */
import { useEffect } from 'react';
import { toast } from 'sonner';

const STORAGE_KEY = 'ds_highlight_target';

export function setHighlightTarget(descriptor) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(descriptor));
  } catch {}
}

export function getHighlightTarget() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearHighlightTarget() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {}
}

/**
 * useHighlightTarget — call from a page mount. Highlights the target button
 * once the page has rendered.
 *
 * @param {string} pageKey — only run if descriptor.path === pageKey
 * @param {object} options
 * @param {(dialogName: string) => void} [options.openDialog] — called if
 *   the descriptor specifies a dialog to open before highlighting
 * @param {boolean} [options.gameInProgress] — pages can pass true once game
 *   data has loaded; we delay until then
 */
export function useHighlightTarget(pageKey, { openDialog, gameInProgress = true } = {}) {
  useEffect(() => {
    if (!gameInProgress) return; // wait for next render where it's true
    const target = getHighlightTarget();
    if (!target || target.path !== pageKey) return;

    let highlighted = null;
    let cleanupTimer = null;
    let pointerHandler = null;

    const apply = () => {
      const el = document.querySelector(`[data-testid="${target.testId}"]`);
      if (!el) return false;
      el.classList.add('ds-highlight-target');
      try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
      highlighted = el;
      // Clear on any pointerdown (button press OR anywhere else)
      pointerHandler = () => clearHighlight();
      window.addEventListener('pointerdown', pointerHandler, { once: true });
      // Also auto-clear after 8 seconds even if user doesn't tap
      cleanupTimer = setTimeout(clearHighlight, 8000);
      return true;
    };

    const clearHighlight = () => {
      if (highlighted) {
        highlighted.classList.remove('ds-highlight-target');
        highlighted = null;
      }
      if (cleanupTimer) {
        clearTimeout(cleanupTimer);
        cleanupTimer = null;
      }
      if (pointerHandler) {
        window.removeEventListener('pointerdown', pointerHandler);
        pointerHandler = null;
      }
    };

    // Open dialog first if needed (e.g. Hits dialog for 1B/2B/HR)
    if (target.openDialog && openDialog) {
      openDialog(target.openDialog);
      // Give the dialog 500ms to mount + animate, then highlight
      setTimeout(apply, 500);
    } else {
      // No dialog needed — small delay for any layout settling
      setTimeout(apply, 250);
    }

    if (target.hint) {
      toast.info(target.hint, { duration: 5000 });
    }

    // Clear sessionStorage so it doesn't replay on later navigations
    clearHighlightTarget();

    return () => clearHighlight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, gameInProgress]);
}
